import React, { useEffect, useMemo, useState } from "react";
import { FiDownload, FiInfo, FiPlus, FiSend, FiTrash2, FiX } from "react-icons/fi";
import {
  actionCatalog,
  directory,
  monthDays,
  policies,
  projects,
} from "../data/hrmMockData";
import ComponentLoader from "./ComponentLoader";

const MAX_DAILY_HOURS = 8;
const LOSS_OF_PAY_VALUE = "Loss of Pay";
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const REQUEST_TYPE_OPTIONS = [
  "General Query",
  "Assets",
  "Payroll",
  "Finance",
  "Operations",
  "Leave & Attendance",
  "Time Entries",
  "Human Resource",
  "IT Support",
  "Access Request",
  "Expense",
  "Policy",
  "Referral",
  "Other",
];
const getStatusClass = (request) => request.statusTone || String(request.status || "pending").toLowerCase().replace(/\s+/g, "-");
const getDateInputValue = (date = new Date()) =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);

const isWeekendDateValue = (dateValue) => {
  const date = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return false;
  }
  return date.getDay() === 0 || date.getDay() === 6;
};

const getCurrentPayrollCaseSubject = () => {
  const monthLabel = new Date().toLocaleDateString("en-IN", { month: "short", year: "numeric" });
  return `Payroll finance query - ${monthLabel}`;
};

const getCurrentAttendanceCaseSubject = () => {
  const monthLabel = new Date().toLocaleDateString("en-IN", { month: "short", year: "numeric" });
  return `Attendance review required - ${monthLabel}`;
};

const createWorkItem = () => ({
  id: `work-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  rowKey: `work-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  hours: "",
  project: "",
  task: "",
  description: "",
});

const isLeaveTimeItem = (item = {}) => {
  if (
    item.isLeaveEntry ||
    item.isLeaveTimeEntry ||
    item.leaveEntry ||
    item.leaveTimeEntry ||
    item.isLeave
  ) {
    return true;
  }

  const descriptor = String(`${item.description || ""} ${item.source || ""} ${item.entryType || ""}`)
    .trim()
    .toLowerCase();
  return (
    descriptor.includes("auto-created from approved leave") ||
    descriptor.includes("took leave") ||
    descriptor.includes("leave time entry")
  );
};

const createBulkTimeRow = () => ({
  id: `bulk-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  project: "",
  task: "",
  description: "",
  hours: "",
});

const REVIEW_COMMENT_KEYS = [
  "reviewComment",
  "reviewComments",
  "reviewNote",
  "reviewNotes",
  "rejectionReason",
  "rejectionComment",
  "rejectedReason",
  "managerComment",
  "approverComment",
  "approvalComment",
  "comments",
  "comment",
  "Review_Comment__c",
  "Review_Comments__c",
  "Rejection_Reason__c",
  "Rejection_Comment__c",
  "Approver_Comment__c",
  "Manager_Comment__c",
];

const normalizeReviewCommentValue = (value) => {
  if (value === undefined || value === null) {
    return "";
  }
  if (typeof value === "string" || typeof value === "number") {
    const cleanedText = String(value)
      .replace(/^\*+\s*/, "")
      .replace(/\s+,/g, ",")
      .replace(/,\s*/g, ", ")
      .trim();
    return /^[*._\-\s]+$/.test(cleanedText) ? "" : cleanedText;
  }
  if (Array.isArray(value)) {
    return value.map(normalizeReviewCommentValue).filter(Boolean).join(" ");
  }
  if (typeof value === "object") {
    for (const key of REVIEW_COMMENT_KEYS.concat(["body", "message", "note", "reason", "text", "value"])) {
      const normalizedValue = normalizeReviewCommentValue(value[key]);
      if (normalizedValue) {
        return normalizedValue;
      }
    }
  }
  return "";
};

const getReviewComment = (source) => {
  if (!source || typeof source !== "object") {
    return normalizeReviewCommentValue(source);
  }

  for (const key of REVIEW_COMMENT_KEYS) {
    const normalizedValue = normalizeReviewCommentValue(source[key]);
    if (normalizedValue) {
      return normalizedValue;
    }
  }

  return "";
};

const normalizeDayEntry = (entry) => {
  if (entry?.isOutsideMonth) {
    return { ...entry, items: [] };
  }

  if (entry?.items?.length) {
    const items = entry.items.map((item) => {
      const fallbackItem = createWorkItem();
      return {
        id: item.id || fallbackItem.id,
        timeEntryId: item.timeEntryId || item.timeSheetEntryId || item.timesheetEntryId || item.timeCardEntryId || item.timecardEntryId || item.entryId || item.recordId || item.salesforceId || item.sfId || item.id || "",
        recordId: item.recordId || item.salesforceId || item.sfId || item.id || "",
        salesforceId: item.salesforceId || item.sfId || item.recordId || item.id || "",
        timeCardEntryId: item.timeCardEntryId || item.timecardEntryId || item.timeEntryId || item.id || "",
        rowKey: item.rowKey || item.clientRowId || item.id || fallbackItem.rowKey,
        workDate: item.workDate || entry.workDate || "",
        hours: item.hours || "",
        project: item.project || "",
        projectId: item.projectId || "",
        task: item.taskTitle || item.title || item.task || "",
        taskId: item.taskId || "",
        description: item.description || item.Desc__c || "",
        approvedHours: item.approvedHours ?? item.approvedHoursValue ?? "",
        lockRows: Boolean(item.lockRows),
        isLeaveEntry: Boolean(item.isLeaveEntry || item.isLeaveTimeEntry || item.leaveEntry || item.leaveTimeEntry || item.isLeave),
        readOnly: Boolean(item.readOnly),
        canEdit: item.canEdit,
        canSubmit: item.canSubmit,
        reviewComment: getReviewComment(item),
        status: item.status || entry.status || "",
      };
    });
    return {
      workDate: entry.workDate || "",
      status: entry.status || "",
      submittedOn: entry.submittedOn || "",
      reviewComment: getReviewComment(entry),
      deletedEntries: Array.isArray(entry.deletedEntries) ? entry.deletedEntries : [],
      items,
    };
  }

  if (entry && (entry.hours || entry.project || entry.task || entry.description || entry.status)) {
    const fallbackItem = createWorkItem();
    const item = {
      id: entry.id || fallbackItem.id,
      timeEntryId: entry.timeEntryId || entry.timeSheetEntryId || entry.timesheetEntryId || entry.timeCardEntryId || entry.timecardEntryId || entry.entryId || entry.recordId || entry.salesforceId || entry.sfId || entry.id || "",
      recordId: entry.recordId || entry.salesforceId || entry.sfId || entry.id || "",
      salesforceId: entry.salesforceId || entry.sfId || entry.recordId || entry.id || "",
      timeCardEntryId: entry.timeCardEntryId || entry.timecardEntryId || entry.timeEntryId || entry.id || "",
      rowKey: entry.rowKey || entry.clientRowId || entry.id || fallbackItem.rowKey,
      workDate: entry.workDate || "",
      hours: entry.hours || "",
      project: entry.project || "",
      projectId: entry.projectId || "",
      task: entry.taskTitle || entry.title || entry.task || "",
      taskId: entry.taskId || "",
      description: entry.description || entry.Desc__c || "",
      approvedHours: entry.approvedHours ?? entry.approvedHoursValue ?? "",
      lockRows: Boolean(entry.lockRows),
      isLeaveEntry: Boolean(entry.isLeaveEntry || entry.isLeaveTimeEntry || entry.leaveEntry || entry.leaveTimeEntry || entry.isLeave),
      readOnly: Boolean(entry.readOnly),
      canEdit: entry.canEdit,
      canSubmit: entry.canSubmit,
      reviewComment: getReviewComment(entry),
      status: entry.status || "",
    };
    return {
      workDate: entry.workDate || "",
      status: entry.status || "",
      submittedOn: entry.submittedOn || "",
      reviewComment: getReviewComment(entry),
      deletedEntries: Array.isArray(entry.deletedEntries) ? entry.deletedEntries : [],
      items: [item],
    };
  }

  const fallbackItem = createWorkItem();
  return {
    workDate: entry?.workDate || fallbackItem.workDate || "",
    status: entry?.status || "",
    submittedOn: entry?.submittedOn || "",
    reviewComment: getReviewComment(entry),
    deletedEntries: Array.isArray(entry?.deletedEntries) ? entry.deletedEntries : [],
    items: [
      {
        ...fallbackItem,
        workDate: entry?.workDate || "",
        status: entry?.status || "",
      },
    ],
  };
};

const normalizeWeekEntries = (entries = {}) =>
  monthDays.reduce(
    (normalizedEntries, day) => ({
      ...normalizedEntries,
      [day.id]: normalizeDayEntry(entries[day.id]),
    }),
    {}
  );

const getDayEntryHours = (entry) => {
  if (entry?.isOutsideMonth) {
    return 0;
  }
  if (entry?.items?.length) {
    return entry.items.reduce((sum, item) => sum + Number(item.hours || 0), 0);
  }
  return Number(entry?.hours || 0);
};

const formatDayEntryHours = (entry) => {
  if (entry?.isOutsideMonth || !entry?.workDate) {
    return "";
  }
  const hours = getDayEntryHours(entry);
  return hours ? String(Number(hours.toFixed(2))) : "-";
};

const formatHoursNumber = (value) => String(Number(Number(value || 0).toFixed(2)));

const formatDayHoursSummary = (entry) => {
  const submittedHours = getDayEntryHours(entry);
  return submittedHours ? `${formatHoursNumber(submittedHours)} / ${MAX_DAILY_HOURS} hours` : `0 / ${MAX_DAILY_HOURS} hours`;
};

const formatWorkDateDay = (entry) => {
  if (entry?.isOutsideMonth || !entry?.workDate) {
    return "";
  }
  const isoDateParts = String(entry.workDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!isoDateParts) {
    return "";
  }
  const monthLabels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const monthLabel = monthLabels[Number(isoDateParts[2]) - 1];
  const dayNumber = Number(isoDateParts[3]);
  return Number.isFinite(dayNumber) && monthLabel ? `${dayNumber}-${monthLabel}` : "";
};

const getEntryStatus = (entry, month) => entry?.status || (month?.status === "Approved" ? "Approved" : "Draft");

const getStatusLabel = (status) => (status === "Submitted" ? "In Review" : status || "Draft");

const formatStatusWithHours = (status, entry) => {
  const label = getStatusLabel(status);
  const hours = formatDayEntryHours(entry);
  return hours && hours !== "-" ? `${label} (${hours})` : label;
};

const getStatusChipClass = (status) => {
  if (status === "Submitted") {
    return "new-hrm-chip--amber";
  }
  if (status === "Approved") {
    return "new-hrm-chip--green";
  }
  if (status === "Rejected") {
    return "new-hrm-chip--red";
  }
  return "new-hrm-chip--blue";
};

const getStatusCssToken = (status) => String(status || "Draft").toLowerCase().replace(/\s+/g, "-");

const isRejectedEntryStatus = (status) =>
  String(status || "").trim().toLowerCase().replace(/[\s_-]+/g, "") === "rejected";

const normalizeTimeStatusKey = (status) =>
  String(status || "").trim().toLowerCase().replace(/[\s_-]+/g, "");

const isEditableTimeStatus = (status) => {
  const normalizedStatus = normalizeTimeStatusKey(status);
  return !normalizedStatus || normalizedStatus === "draft" || normalizedStatus === "rejected";
};

const isLockedTimeStatus = (status) => !isEditableTimeStatus(status);

const isReadOnlyTimeItem = (item = {}, status = "", dayLocked = false) =>
  Boolean(dayLocked || isLeaveTimeItem(item) || item.lockRows || item.readOnly || item.canEdit === false || isLockedTimeStatus(status));

const markRejectedTimeEntryForCorrection = (entry = {}) => {
  const items = entry.items || [];
  const hasRejectedStatus =
    isRejectedEntryStatus(entry.status) || items.some((item) => isRejectedEntryStatus(item.status));

  if (!hasRejectedStatus) {
    return entry;
  }

  return {
    ...entry,
    status: "Draft",
    items: items.map((item) => ({
      ...item,
      status: isRejectedEntryStatus(item.status) ? "Draft" : item.status,
      reviewComment: isRejectedEntryStatus(item.status) ? "" : item.reviewComment,
    })),
  };
};

const markRejectedWeekEntriesForCorrection = (entries = {}) =>
  Object.fromEntries(
    Object.entries(entries).map(([dayId, entry]) => [dayId, markRejectedTimeEntryForCorrection(entry)])
  );

const normalizeProjectColor = (value) => {
  const color = String(value || "").trim();
  return /^#[0-9a-fA-F]{6}$/.test(color) ? color : "";
};

const getWorkItemProjectColor = (item = {}, projectOptions = []) => {
  const directColor = normalizeProjectColor(item.projectColor || item.projectColour || item.color || item.colour);
  if (directColor) {
    return directColor;
  }
  const project = projectOptions.find((option) =>
    (item.projectId && String(option.id) === String(item.projectId)) ||
    (item.project && option.name === item.project)
  );
  return normalizeProjectColor(project?.colorCode || project?.color || project?.colour || project?.projectColor);
};

const normalizeComparableText = (value) => String(value || "").trim().toLowerCase();

const getOptionRecordId = (value) => String(value || "").trim();

const getProjectOptionId = (project = {}) => getOptionRecordId(project.id || project.projectId || project.Project__c);

const getTaskProjectId = (task = {}) => getOptionRecordId(task.projectId || task.Project__c || task.project?.id || task.Project__r?.Id);

const getTaskProjectName = (task = {}) =>
  normalizeComparableText(task.projectName || task.projectTitle || task.project || task.Project__r?.Name || task.Project__r?.Title__c);

const findProjectOptionForItem = (item = {}, projectOptions = []) => {
  const itemProjectId = getOptionRecordId(item.projectId || item.Project__c);
  const itemProjectName = normalizeComparableText(item.project || item.projectName);
  return projectOptions.find((project) => {
    const projectId = getProjectOptionId(project);
    return (itemProjectId && projectId === itemProjectId) || (itemProjectName && normalizeComparableText(project.name) === itemProjectName);
  });
};

const isTaskForProject = (task = {}, project = {}) => {
  const projectId = getProjectOptionId(project);
  const taskProjectId = getTaskProjectId(task);
  if (projectId && taskProjectId) {
    return projectId === taskProjectId;
  }

  const projectName = normalizeComparableText(project.name || project.projectName);
  const taskProjectName = getTaskProjectName(task);
  return Boolean(projectName && taskProjectName && projectName === taskProjectName);
};

const getTaskOptionsForProject = (project = {}, taskOptions = []) =>
  project ? taskOptions.filter((task) => isTaskForProject(task, project)) : [];

const findTaskOptionForProject = (taskName, project = {}, taskOptions = []) => {
  const normalizedTaskName = normalizeComparableText(taskName);
  if (!normalizedTaskName || !project) {
    return undefined;
  }
  return getTaskOptionsForProject(project, taskOptions).find((task) => normalizeComparableText(task.name) === normalizedTaskName);
};

const getTaskDatalistId = (prefix, dayId, itemId) =>
  `${prefix}-${dayId}-${itemId}`.replace(/[^a-zA-Z0-9_-]/g, "-");

const getWorkItemRowKey = (item = {}) => item.rowKey || item.clientRowId || item.id;

const withDeletedWorkItem = (entry = {}, item = {}) => {
  const itemStatus = item.status || entry.status || "Draft";
  if (!item || !Object.keys(item).length || isReadOnlyTimeItem(item, itemStatus)) {
    return entry;
  }

  return {
    ...entry,
    deletedEntries: [
      ...(Array.isArray(entry.deletedEntries) ? entry.deletedEntries : []),
      {
        ...item,
        workDate: item.workDate || entry.workDate || "",
        status: itemStatus,
      },
    ],
  };
};

const getSelectedSubmitHourIssues = (cellIds = [], timesheet = [], month, monthId) =>
  cellIds
    .filter((cellId) => !monthId || cellId.startsWith(`${monthId}:`))
    .map((cellId) => {
      const [, weekId, dayId] = cellId.split(":");
      const week = timesheet.find((monthWeek) => monthWeek.id === weekId);
      const day = monthDays.find((monthDay) => monthDay.id === dayId);
      const entry = week?.entries?.[dayId];
      const status = getEntryStatus(entry, month);
      const isOutsideMonth = entry?.isOutsideMonth || !entry?.workDate;
      const workDate = entry?.workDate || getWorkDateForWeekDay(monthId, week, dayId);
      if (!week || !day || isOutsideMonth || !workDate || isLockedTimeStatus(status)) {
        return null;
      }

      const hours = getDayEntryHours(entry);
      if (hours === MAX_DAILY_HOURS) {
        return null;
      }

      return {
        label: `${week.label} ${day.label}`,
        hours: Number(hours.toFixed(2)),
      };
    })
    .filter(Boolean);

const formatSubmitHourIssue = (issue) => `${issue.label} (${issue.hours || 0}h)`;

const getWorkDateForWeekDay = (monthId, week, dayKey) => {
  const [year, month] = String(monthId || "").split("-").map(Number);
  const dayIndexByKey = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
  const targetDayIndex = dayIndexByKey[dayKey];
  const rangeMatch = String(week?.range || "").match(/^(\d{1,2})-(\d{1,2})/);
  const startDay = Number(rangeMatch?.[1]);
  const endDay = Number(rangeMatch?.[2]);

  if (!year || !month || targetDayIndex === undefined || !startDay || !endDay) {
    return "";
  }

  const daysInMonth = new Date(year, month, 0).getDate();
  for (let dayOfMonth = startDay; dayOfMonth <= Math.min(endDay, daysInMonth); dayOfMonth += 1) {
    const date = new Date(year, month - 1, dayOfMonth);
    if (date.getDay() === targetDayIndex) {
      return `${year}-${String(month).padStart(2, "0")}-${String(dayOfMonth).padStart(2, "0")}`;
    }
  }

  return "";
};

const formatSelectedTimesheetDate = (workDate) => {
  const date = new Date(`${workDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return workDate;
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    weekday: "short",
  });
};

const getSelectedTimesheetDates = (cellIds = [], timesheet = [], monthId) =>
  cellIds
    .filter((cellId) => !monthId || cellId.startsWith(`${monthId}:`))
    .map((cellId) => {
      const [, weekId, dayId] = cellId.split(":");
      const week = timesheet.find((monthWeek) => monthWeek.id === weekId);
      const entry = week?.entries?.[dayId];
      const workDate = entry?.workDate || getWorkDateForWeekDay(monthId, week, dayId);
      if (!week || entry?.isOutsideMonth || !workDate) {
        return null;
      }

      return {
        cellId,
        label: formatSelectedTimesheetDate(workDate),
        workDate,
      };
    })
    .filter(Boolean)
    .sort((firstDate, secondDate) => firstDate.workDate.localeCompare(secondDate.workDate));

const calculateLeaveDays = (startDate, endDate) => {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
    return 0;
  }

  return Math.floor((end - start) / ONE_DAY_MS) + 1;
};

const toIsoDateKey = (value) => {
  if (!value) {
    return "";
  }

  const rawValue = String(value).trim();
  const isoMatch = rawValue.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) {
    return isoMatch[1];
  }

  const date = new Date(rawValue.includes("T") ? rawValue : `${rawValue}T00:00:00`);
  return Number.isNaN(date.getTime()) ? "" : getDateInputValue(date);
};

const getHolidayDateKey = (holiday) =>
  toIsoDateKey(holiday?.dateKey || holiday?.rawDate || holiday?.holidayDate || holiday?.Holiday_Date__c || holiday?.date);

const getNextDateKey = (dateKey) => {
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  date.setDate(date.getDate() + 1);
  return getDateInputValue(date);
};

const getWeekendDayName = (dateKey) => {
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleDateString("en-GB", { weekday: "long" });
};

const getLeaveWeekendConflicts = (startDate, endDate) => {
  const startKey = toIsoDateKey(startDate);
  const endKey = toIsoDateKey(endDate);

  if (!startKey || !endKey || endKey < startKey) {
    return [];
  }

  const conflicts = [];
  for (let dateKey = startKey; dateKey && dateKey <= endKey; dateKey = getNextDateKey(dateKey)) {
    const date = new Date(`${dateKey}T00:00:00`);
    const day = date.getDay();
    if (day === 0 || day === 6) {
      conflicts.push({
        date: dateKey,
        name: getWeekendDayName(dateKey),
      });
    }
  }

  return conflicts;
};

const formatWeekendConflictMessage = (conflicts = []) => {
  if (!conflicts.length) {
    return "";
  }

  const weekendLabels = conflicts.slice(0, 3).map((day) => `${day.name} (${day.date})`);
  const remainingCount = conflicts.length - weekendLabels.length;
  const suffix = remainingCount > 0 ? ` and ${remainingCount} more` : "";
  return `Leave cannot be applied on weekends: ${weekendLabels.join(", ")}${suffix}. Split the request around weekends.`;
};

const getLeaveHolidayConflicts = (startDate, endDate, holidays = []) => {
  const startKey = toIsoDateKey(startDate);
  const endKey = toIsoDateKey(endDate);

  if (!startKey || !endKey || endKey < startKey || !holidays.length) {
    return [];
  }

  const holidaysByDate = holidays.reduce((map, holiday) => {
    const dateKey = getHolidayDateKey(holiday);
    if (dateKey && !map.has(dateKey)) {
      map.set(dateKey, holiday);
    }
    return map;
  }, new Map());
  const conflicts = [];

  for (let dateKey = startKey; dateKey && dateKey <= endKey; dateKey = getNextDateKey(dateKey)) {
    const holiday = holidaysByDate.get(dateKey);
    if (holiday) {
      conflicts.push({
        dateKey,
        name: holiday.name || holiday.title || holiday.Name || "Holiday",
        date: holiday.date || dateKey,
      });
    }
  }

  return conflicts;
};

const formatHolidayConflictMessage = (conflicts = []) => {
  if (!conflicts.length) {
    return "";
  }

  const holidayLabels = conflicts.slice(0, 3).map((holiday) => `${holiday.name} (${holiday.date})`);
  const remainingCount = conflicts.length - holidayLabels.length;
  const suffix = remainingCount > 0 ? ` and ${remainingCount} more` : "";
  return `Leave cannot be applied on company holidays: ${holidayLabels.join(", ")}${suffix}. Split the request around holidays.`;
};

const isBlockingLeaveRequestStatus = (status) => {
  const normalizedStatus = String(status || "").trim().toLowerCase();
  return normalizedStatus && !normalizedStatus.includes("reject") && !normalizedStatus.includes("cancel");
};

const getLeaveRequestDays = (request = {}) => {
  const directValue = Number(request?.leaveDays ?? request?.days ?? request?.durationDays);
  if (Number.isFinite(directValue)) {
    return directValue;
  }
  const parsedValue = parseFloat(String(request?.duration || ""));
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const isHalfDayLeaveRequest = (request = {}) => {
  const durationText = String(request?.duration || request?.durationType || request?.Duration_Type__c || "").toLowerCase();
  return getLeaveRequestDays(request) === 0.5 || durationText.includes("half");
};

const getLeaveOverlapConflicts = (startDate, endDate, leaveRequests = [], requestedLeaveDays = 1) => {
  const startKey = toIsoDateKey(startDate);
  const endKey = toIsoDateKey(endDate);

  if (!startKey || !endKey || endKey < startKey) {
    return [];
  }

  const requestedDays = Number(requestedLeaveDays || 0);
  const canShareHalfDayDate = startKey === endKey && requestedDays === 0.5;
  const existingSameDayHalfDays = (leaveRequests || []).reduce((sum, request) => {
    if (!isBlockingLeaveRequestStatus(request?.status)) {
      return sum;
    }
    const requestStart = toIsoDateKey(request?.startDateKey || request?.startDate || request?.date);
    const requestEnd = toIsoDateKey(request?.endDateKey || request?.endDate || request?.date || requestStart) || requestStart;
    if (requestStart === startKey && requestEnd === endKey && isHalfDayLeaveRequest(request)) {
      return sum + 0.5;
    }
    return sum;
  }, 0);

  return (leaveRequests || []).filter((request) => {
    if (!isBlockingLeaveRequestStatus(request?.status)) {
      return false;
    }
    const requestStart = toIsoDateKey(request?.startDateKey || request?.startDate || request?.date);
    const requestEnd = toIsoDateKey(request?.endDateKey || request?.endDate || request?.date || requestStart) || requestStart;
    const overlaps = requestStart && requestEnd && requestStart <= endKey && requestEnd >= startKey;
    if (!overlaps) {
      return false;
    }
    if (
      canShareHalfDayDate &&
      requestStart === startKey &&
      requestEnd === endKey &&
      isHalfDayLeaveRequest(request) &&
      existingSameDayHalfDays + requestedDays <= 1
    ) {
      return false;
    }
    return true;
  });
};

const formatLeaveOverlapMessage = (conflicts = []) => {
  if (!conflicts.length) {
    return "";
  }

  const request = conflicts[0];
  const range = request.endDate && request.endDate !== request.date ? `${request.date} - ${request.endDate}` : request.date;
  return `Leave already exists for ${range} (${request.status}). Cancel or reject the existing request before applying again.`;
};

const formatLeaveDuration = (days) => `${days} ${days === 1 ? "day" : "days"}`;

const isLossOfPayType = (value) => {
  const normalizedValue = String(value || "").toLowerCase();
  return normalizedValue === "lop" || normalizedValue.includes("loss of pay");
};

const getPaidLeaveBalanceTotal = (leaveBalances = []) =>
  leaveBalances.reduce((sum, balance) => {
    const label = balance?.label || balance?.leaveType || balance?.type || "";
    if (isLossOfPayType(label)) {
      return sum;
    }
    const remaining = Number(balance?.remaining || balance?.balance || 0);
    return sum + Math.max(Number.isFinite(remaining) ? remaining : 0, 0);
  }, 0);

const normalizeOptionKey = (value) => String(value || "").trim().toLowerCase();

const buildLeaveTypeOptions = (leaveBalances = [], leaveTypes = []) => {
  const positiveBalanceOptions = leaveBalances.map((balance) => {
    const label = balance?.label || balance?.leaveType || balance?.type;
    const remaining = Number(balance?.remaining ?? balance?.balance ?? 0);
    return {
      label,
      value: label,
      remaining: Number.isFinite(remaining) ? remaining : 0,
    };
  }).filter((option) => option.value && !isLossOfPayType(option.value) && option.remaining > 0);
  const configuredOptions = leaveTypes.map((leaveType) => ({
    label: leaveType.label || leaveType.name || leaveType.value,
    value: leaveType.value || leaveType.label || leaveType.name,
  })).filter((option) => {
    if (!option.value || isLossOfPayType(option.value)) {
      return false;
    }
    if (!leaveBalances.length) {
      return true;
    }
    return positiveBalanceOptions.some((balanceOption) => normalizeOptionKey(balanceOption.value) === normalizeOptionKey(option.value));
  });
  const fallbackOptions = ["Casual Leave", "Earned Leave", "Sick Leave", "Comp Off", "Elective Leave"].map((option) => ({
    label: option,
    value: option,
  }));
  const options = positiveBalanceOptions.length
    ? positiveBalanceOptions
    : configuredOptions.length
      ? configuredOptions
      : leaveBalances.length
        ? []
        : fallbackOptions;
  const uniqueOptions = [];
  const optionKeys = new Set();

  options.forEach((option) => {
    const key = normalizeOptionKey(option.value);
    if (!optionKeys.has(key)) {
      optionKeys.add(key);
      uniqueOptions.push(option);
    }
  });

  if (!uniqueOptions.some((option) => isLossOfPayType(option.value))) {
    uniqueOptions.push({
      label: "LOP - Loss of Pay",
      value: LOSS_OF_PAY_VALUE,
    });
  }

  return uniqueOptions;
};

const ActionModal = ({
  action,
  currentPayslip,
  dataLoading = false,
  directoryPeople = directory,
  expenses,
  holidays = [],
  leaveBalances,
  leaveTypes = [],
  leaveRequests = [],
  monthTimesheet,
  onboardingTasks,
  onClose,
  onCancelExpense,
  onCancelLeave,
  onDownloadPayslip,
  onFillSelectedTime,
  onSubmitExpense,
  onSubmitRequest,
  onSubmitLeave,
  onSubmitTime,
  onSubmitTimesheetApproval,
  onTimeCellEdit,
  onTimesheetCellToggle,
  onTimesheetMonthChange,
  onToggleTask,
  operationLoading = "",
  filterLoading = "",
  projectOptions = projects,
  selectedPolicy,
  selectedTimesheetCellIds = [],
  selectedTimesheetCellCount = 0,
  selectedTimesheetMonth,
  selectedTimesheetMonthId,
  selectedExpense = null,
  selectedLeaveRequest = null,
  selectedTimeDayId,
  selectedTimeWeek,
  taskOptions = [],
  timesheetMonths = [],
  isTimesheetLocked,
  timesheetLockMessage,
  requests,
}) => {
  const [leaveStartDate, setLeaveStartDate] = useState(getDateInputValue());
  const [leaveEndDate, setLeaveEndDate] = useState(getDateInputValue());
  const [leaveType, setLeaveType] = useState("Casual Leave");
  const [leaveHalfDay, setLeaveHalfDay] = useState(false);
  const [leaveDescription, setLeaveDescription] = useState("");
  const [leaveOooAdded, setLeaveOooAdded] = useState(false);
  const [leaveStep, setLeaveStep] = useState(1);
  const [weekDraft, setWeekDraft] = useState(() => normalizeWeekEntries(selectedTimeWeek?.entries));
  const [expenseTitle, setExpenseTitle] = useState("");
  const [expenseAmount, setExpenseAmount] = useState("");
  const [expenseDescription, setExpenseDescription] = useState("");
  const [expenseDate, setExpenseDate] = useState(getDateInputValue());
  const [expenseBillFile, setExpenseBillFile] = useState(null);
  const [requestSubject, setRequestSubject] = useState("");
  const [requestDescription, setRequestDescription] = useState("");
  const [requestType, setRequestType] = useState(REQUEST_TYPE_OPTIONS[0]);
  const [bulkRows, setBulkRows] = useState(() => [{ ...createBulkTimeRow(), hours: "8" }]);

  const actionMeta = useMemo(() => actionCatalog[action] || actionCatalog.hr, [action]);
  const daysOverDailyLimit = useMemo(
    () => monthDays.filter((day) => getDayEntryHours(weekDraft[day.id]) > MAX_DAILY_HOURS),
    [weekDraft]
  );
  const hasDailyLimitError = daysOverDailyLimit.length > 0;
  const leaveStatus = String(selectedLeaveRequest?.status || "").trim().toLowerCase();
  const isLeaveRejected = leaveStatus.includes("reject");
  const isLeaveApproved = leaveStatus.includes("approved");
  const isLeaveEditable = !selectedLeaveRequest || Boolean(selectedLeaveRequest.canEdit);
  const isLeaveReadOnly = Boolean(selectedLeaveRequest) && !isLeaveEditable;
  const canCancelLeave = Boolean(selectedLeaveRequest?.id && selectedLeaveRequest?.canCancel);
  const leaveBalancesForRequest = useMemo(() => {
    if (!selectedLeaveRequest?.canEdit) {
      return leaveBalances;
    }
    const requestTypeKey = normalizeOptionKey(selectedLeaveRequest.leaveType);
    const requestDays = Number(selectedLeaveRequest.leaveDays || 0);
    if (!requestTypeKey || !Number.isFinite(requestDays) || requestDays <= 0) {
      return leaveBalances;
    }
    return leaveBalances.map((balance) => {
      const balanceTypeKey = normalizeOptionKey(balance?.label || balance?.leaveType || balance?.type);
      if (balanceTypeKey !== requestTypeKey) {
        return balance;
      }
      return {
        ...balance,
        remaining: Number((Number(balance.remaining || 0) + requestDays).toFixed(2)),
        pendingReserved: Math.max(Number(balance.pendingReserved || 0) - requestDays, 0),
      };
    });
  }, [leaveBalances, selectedLeaveRequest]);
  const leaveTypeOptions = useMemo(() => buildLeaveTypeOptions(leaveBalancesForRequest, leaveTypes), [leaveBalancesForRequest, leaveTypes]);
  const isSingleDayLeave = Boolean(leaveStartDate && leaveEndDate && leaveStartDate === leaveEndDate);
  const isHalfDayLeave = isSingleDayLeave && leaveHalfDay;
  const leaveDays = useMemo(
    () => (isHalfDayLeave ? 0.5 : calculateLeaveDays(leaveStartDate, leaveEndDate)),
    [isHalfDayLeave, leaveEndDate, leaveStartDate]
  );
  const selectedLeaveBalance = useMemo(
    () => (leaveBalancesForRequest || []).find((balance) => normalizeOptionKey(balance.label) === normalizeOptionKey(leaveType)),
    [leaveBalancesForRequest, leaveType]
  );
  const selectedLeaveRemaining = Number(selectedLeaveBalance?.remaining || 0);
  const selectedLeavePendingReserved = Number(selectedLeaveBalance?.pendingReserved || 0);
  const isLossOfPayLeave = isLossOfPayType(leaveType);
  const paidLeaveBalanceTotal = useMemo(() => getPaidLeaveBalanceTotal(leaveBalancesForRequest), [leaveBalancesForRequest]);
  const hasKnownPaidLeaveBalance = Boolean(selectedLeaveBalance) && !isLossOfPayLeave;
  const hasInsufficientLeaveBalance = hasKnownPaidLeaveBalance && leaveDays > selectedLeaveRemaining;
  const hasLopBalanceBlock = isLossOfPayLeave && paidLeaveBalanceTotal > 0;
  const leaveWeekendConflicts = useMemo(
    () => getLeaveWeekendConflicts(leaveStartDate, leaveEndDate),
    [leaveEndDate, leaveStartDate]
  );
  const hasWeekendConflict = leaveWeekendConflicts.length > 0;
  const leaveWeekendMessage = useMemo(
    () => formatWeekendConflictMessage(leaveWeekendConflicts),
    [leaveWeekendConflicts]
  );
  const leaveHolidayConflicts = useMemo(
    () => getLeaveHolidayConflicts(leaveStartDate, leaveEndDate, holidays),
    [holidays, leaveEndDate, leaveStartDate]
  );
  const hasHolidayConflict = leaveHolidayConflicts.length > 0;
  const leaveHolidayMessage = useMemo(
    () => formatHolidayConflictMessage(leaveHolidayConflicts),
    [leaveHolidayConflicts]
  );
  const leaveRequestsForValidation = useMemo(
    () => selectedLeaveRequest
      ? leaveRequests.filter((request) => String(request.id) !== String(selectedLeaveRequest.id))
      : leaveRequests,
    [leaveRequests, selectedLeaveRequest]
  );
  const leaveOverlapConflicts = useMemo(
    () => getLeaveOverlapConflicts(leaveStartDate, leaveEndDate, leaveRequestsForValidation, leaveDays),
    [leaveDays, leaveEndDate, leaveRequestsForValidation, leaveStartDate]
  );
  const hasLeaveOverlapConflict = leaveOverlapConflicts.length > 0;
  const leaveOverlapMessage = useMemo(
    () => formatLeaveOverlapMessage(leaveOverlapConflicts),
    [leaveOverlapConflicts]
  );
  const hasLeaveValidationError = hasInsufficientLeaveBalance || hasLopBalanceBlock || hasWeekendConflict || hasHolidayConflict || hasLeaveOverlapConflict;
  const leaveBalanceMessage = isLossOfPayLeave
    ? hasLopBalanceBlock
      ? `Use the remaining ${formatLeaveDuration(Number(paidLeaveBalanceTotal.toFixed(2)))} paid leave balance before applying LOP.`
      : "Loss of Pay does not use leave balance."
    : hasKnownPaidLeaveBalance
      ? hasInsufficientLeaveBalance
        ? selectedLeavePendingReserved > 0
          ? `Only ${formatLeaveDuration(selectedLeaveRemaining)} available after ${formatLeaveDuration(selectedLeavePendingReserved)} already pending. Split the request across paid leave types before LOP.`
          : `Only ${formatLeaveDuration(selectedLeaveRemaining)} available. Split the request across paid leave types before LOP.`
        : selectedLeavePendingReserved > 0
          ? `${formatLeaveDuration(selectedLeaveRemaining)} available after ${formatLeaveDuration(selectedLeavePendingReserved)} already pending.`
          : `${formatLeaveDuration(selectedLeaveRemaining)} available.`
      : "Balance will be validated before submission.";
  const isApprovedMonth = String(selectedTimesheetMonth?.status || "").toLowerCase() === "approved";
  const selectedMonthStatus = isTimesheetLocked && !isApprovedMonth ? "Closed" : selectedTimesheetMonth?.status || "Draft";
  const selectedBulkTimeDates = useMemo(
    () => getSelectedTimesheetDates(selectedTimesheetCellIds, monthTimesheet, selectedTimesheetMonthId),
    [monthTimesheet, selectedTimesheetCellIds, selectedTimesheetMonthId]
  );
  const bulkRowsValidation = useMemo(() => {
    const normalizedRows = bulkRows.map((row) => {
      const selectedProject = findProjectOptionForItem({ project: row.project }, projectOptions);
      const selectedTask = findTaskOptionForProject(row.task, selectedProject, taskOptions);
      const numericHours = Number(row.hours || 0);
      return {
        ...row,
        numericHours,
        selectedProject,
        selectedTask,
      };
    });
    const totalHours = normalizedRows.reduce((sum, row) => sum + (Number.isFinite(row.numericHours) ? row.numericHours : 0), 0);
    const hasInvalidRow = normalizedRows.some((row) =>
      !row.selectedProject ||
      !row.project.trim() ||
      (row.task.trim() && !row.selectedTask) ||
      !row.description.trim() ||
      !Number.isFinite(row.numericHours) ||
      row.numericHours <= 0
    );

    return {
      hasInvalidRow,
      normalizedRows,
      totalHours,
      hasHourLimitError: totalHours > MAX_DAILY_HOURS,
      isValid: !hasInvalidRow && totalHours > 0 && totalHours <= MAX_DAILY_HOURS,
    };
  }, [bulkRows, projectOptions, taskOptions]);
  const selectedSubmitHourIssues = useMemo(
    () => getSelectedSubmitHourIssues(selectedTimesheetCellIds, monthTimesheet, selectedTimesheetMonth, selectedTimesheetMonthId),
    [monthTimesheet, selectedTimesheetCellIds, selectedTimesheetMonth, selectedTimesheetMonthId]
  );
  const hasSelectedSubmitHourError = selectedSubmitHourIssues.length > 0;
  const selectedSubmitHourMessage = hasSelectedSubmitHourError
    ? `Submit requires exactly ${MAX_DAILY_HOURS} hours on each selected date. Fix ${selectedSubmitHourIssues.slice(0, 3).map(formatSubmitHourIssue).join(", ")}${selectedSubmitHourIssues.length > 3 ? "..." : ""}.`
    : "";
  const operationLoadingLabels = {
    "leave-save": "Submitting leave request",
    "leave-cancel": "Cancelling leave request",
    "time-save": "Saving timesheet",
    "time-submit": "Submitting timesheet",
    "time-bulk-fill": "Filling selected dates",
    "expense-save": "Saving expense",
    "expense-cancel": "Cancelling reimbursement",
    "request-save": "Creating request",
    "payroll-download": "Preparing payslip",
  };
  const actionOperationKeys = {
    leave: ["leave-save", "leave-cancel"],
    time: ["time-save", "time-submit"],
    timeEntry: ["time-save"],
    timesheet: ["time-submit"],
    bulkTime: ["time-bulk-fill"],
    expense: ["expense-save", "expense-cancel"],
    hr: ["request-save"],
    assetRequest: ["request-save"],
    financeRequest: ["request-save"],
    attendanceRequest: ["request-save"],
    payslip: ["payroll-download"],
  };
  const isActionOperationLoading = (actionOperationKeys[action] || []).includes(operationLoading);
  const modalLoadingLabel =
    filterLoading === "time" && dataLoading && (action === "time" || action === "timeEntry" || action === "timesheet")
      ? "Applying month filter"
      : isActionOperationLoading
        ? operationLoadingLabels[operationLoading]
        : "";
  const isActionLoading = Boolean(modalLoadingLabel);
  const cleanExpenseAmount = String(expenseAmount || "").trim();
  const expenseAmountNumber = Number(cleanExpenseAmount);
  const expenseStatus = String(selectedExpense?.status || "").trim().toLowerCase();
  const isExpenseRejected = expenseStatus.includes("reject");
  const isExpenseApproved = expenseStatus.includes("approved");
  const isExpenseMoreInformation = expenseStatus === "need more information";
  const isExpenseEditable = !selectedExpense || Boolean(selectedExpense.canEdit);
  const isExpenseReadOnly = Boolean(selectedExpense) && !isExpenseEditable;
  const canCancelExpense = Boolean(selectedExpense?.id && selectedExpense?.canCancel);
  const hasExpenseAmountInput = cleanExpenseAmount.length > 0;
  const hasValidExpenseAmount = hasExpenseAmountInput && Number.isFinite(expenseAmountNumber) && expenseAmountNumber > 0;
  const showExpenseAmountError = hasExpenseAmountInput && !hasValidExpenseAmount;
  const timesheetReviewSummary = useMemo(
    () =>
      (monthTimesheet || []).reduce(
        (summary, week) => {
          monthDays.forEach((day) => {
            const entry = week.entries?.[day.id];
            const statusKey = getStatusCssToken(getEntryStatus(entry, selectedTimesheetMonth));
            const summaryKey = statusKey === "in-review" ? "submitted" : statusKey;
            summary.hours += getDayEntryHours(entry);
            summary[summaryKey] = (summary[summaryKey] || 0) + 1;
          });
          return summary;
        },
        { hours: 0, draft: 0, submitted: 0, approved: 0, rejected: 0 }
      ),
    [monthTimesheet, selectedTimesheetMonth]
  );
  const formattedReviewHours = String(Number(timesheetReviewSummary.hours.toFixed(2)));
  const selectedTimeDay = useMemo(
    () => monthDays.find((day) => day.id === selectedTimeDayId),
    [selectedTimeDayId]
  );
  const selectedDayEntry = selectedTimeDayId ? weekDraft[selectedTimeDayId] || { items: [createWorkItem()] } : null;
  const selectedDayStatus = getEntryStatus(selectedDayEntry, selectedTimesheetMonth);
  const selectedDayHours = getDayEntryHours(selectedDayEntry);
  const isSelectedDayLocked =
    isTimesheetLocked ||
    selectedDayEntry?.isOutsideMonth ||
    !selectedDayEntry?.workDate ||
    isLockedTimeStatus(selectedDayStatus);
  const modalTitle = action === "timeEntry" && isSelectedDayLocked
    ? "View Time Entry"
    : action === "leave" && selectedLeaveRequest
      ? isLeaveReadOnly ? "View Leave Request" : "Edit Leave Request"
      : action === "expense" && selectedExpense
        ? isExpenseReadOnly ? "View Reimbursement" : "Update Reimbursement"
        : actionMeta.title;
  const modalDescription =
    action === "timeEntry" && isSelectedDayLocked
        ? "Historical and approved entries are read-only."
        : action === "leave" && selectedLeaveRequest
          ? isLeaveReadOnly ? "This leave request is locked." : "Update the request before it is approved."
          : action === "expense" && selectedExpense
            ? isExpenseReadOnly ? "This reimbursement is locked." : "Add the required details and save your reimbursement."
            : actionMeta.description;

  useEffect(() => {
    setWeekDraft(normalizeWeekEntries(selectedTimeWeek?.entries));
  }, [selectedTimeWeek]);

  useEffect(() => {
    if (action !== "leave") {
      return;
    }
    const today = getDateInputValue();
    const startDate = selectedLeaveRequest?.startDateKey || today;
    const endDate = selectedLeaveRequest?.endDateKey || startDate;
    setLeaveStartDate(startDate);
    setLeaveEndDate(endDate);
    setLeaveType(selectedLeaveRequest?.leaveType || "Casual Leave");
    setLeaveHalfDay(Number(selectedLeaveRequest?.leaveDays || 0) === 0.5);
    setLeaveDescription(selectedLeaveRequest?.description || selectedLeaveRequest?.reason || "");
    setLeaveOooAdded(Boolean(selectedLeaveRequest?.oooAdded));
    setLeaveStep(1);
  }, [action, selectedLeaveRequest]);

  useEffect(() => {
    if (action !== "expense") {
      return;
    }
    setExpenseTitle(selectedExpense?.title || "");
    setExpenseAmount(selectedExpense?.amountValue ? String(selectedExpense.amountValue) : "");
    setExpenseDescription(selectedExpense?.description || "");
    setExpenseDate(selectedExpense?.dateKey || getDateInputValue());
    setExpenseBillFile(null);
  }, [action, selectedExpense]);

  useEffect(() => {
    if (leaveTypeOptions.length && !leaveTypeOptions.some((option) => option.value === leaveType)) {
      setLeaveType(leaveTypeOptions[0].value);
    }
  }, [leaveType, leaveTypeOptions]);

  useEffect(() => {
    if (!isSingleDayLeave && leaveHalfDay) {
      setLeaveHalfDay(false);
    }
  }, [isSingleDayLeave, leaveHalfDay]);

  useEffect(() => {
    if (action === "assetRequest") {
      setRequestType("Assets");
      setRequestSubject("");
      setRequestDescription("");
    } else if (action === "financeRequest") {
      setRequestType("Finance");
      setRequestSubject(getCurrentPayrollCaseSubject());
      setRequestDescription("");
    } else if (action === "attendanceRequest") {
      setRequestType("Leave & Attendance");
      setRequestSubject(getCurrentAttendanceCaseSubject());
      setRequestDescription("");
    } else if (action === "hr") {
      setRequestType(REQUEST_TYPE_OPTIONS[0]);
      setRequestSubject("");
      setRequestDescription("");
    }
  }, [action]);

  if (!action) {
    return null;
  }

  const handleLeaveSubmit = (event) => {
    event.preventDefault();
    if (isActionLoading || isLeaveReadOnly || !leaveDays || !leaveDescription.trim() || hasLeaveValidationError) {
      return;
    }

    if (leaveStep === 1) {
      setLeaveStep(2);
      return;
    }

    if (!leaveOooAdded) {
      return;
    }

    onSubmitLeave({
      requestId: selectedLeaveRequest?.id || "",
      description: leaveDescription.trim(),
      duration: leaveDays,
      durationType: isHalfDayLeave ? "Half Day" : "Full Day",
      endDate: leaveEndDate,
      leaveType,
      oooAdded: leaveOooAdded,
      startDate: leaveStartDate,
    });
  };

  const handleTimeSubmit = (event) => {
    event.preventDefault();
    if (isActionLoading || hasDailyLimitError || isTimesheetLocked) {
      return;
    }

    onSubmitTime({
      weekId: selectedTimeWeek?.id,
      entries: markRejectedWeekEntriesForCorrection(weekDraft),
    });
  };

  const handleTimeEntrySubmit = async (event) => {
    event.preventDefault();
    if (isActionLoading || hasDailyLimitError || isSelectedDayLocked) {
      return;
    }

    const didSave = await onSubmitTime({
      weekId: selectedTimeWeek?.id,
      entries: {
        [selectedTimeDayId]: markRejectedTimeEntryForCorrection(weekDraft[selectedTimeDayId]),
      },
    });
    if (didSave !== false) {
      onClose();
    }
  };

  const handleWeekItemChange = (dayId, itemKey, field, value) => {
    setWeekDraft((current) => {
      const dayEntry = current[dayId] || { items: [createWorkItem()] };

      return {
        ...current,
        [dayId]: {
          ...dayEntry,
          items: (dayEntry.items || [createWorkItem()]).map((item) => {
            if (getWorkItemRowKey(item) !== itemKey) {
              return item;
            }
            if (field === "project") {
              const selectedProject = findProjectOptionForItem({ project: value }, projectOptions);
              const selectedTask = findTaskOptionForProject(item.task, selectedProject, taskOptions);
              return {
                ...item,
                project: value,
                projectId: getProjectOptionId(selectedProject) || "",
                projectColor: selectedProject?.colorCode || selectedProject?.color || selectedProject?.colour || selectedProject?.projectColor || "",
                projectColour: selectedProject?.colorCode || selectedProject?.color || selectedProject?.colour || selectedProject?.projectColor || "",
                task: selectedTask ? item.task : "",
                taskId: selectedTask?.id || "",
              };
            }
            if (field === "task") {
              const selectedProject = findProjectOptionForItem(item, projectOptions);
              const selectedTask = findTaskOptionForProject(value, selectedProject, taskOptions);
              return { ...item, task: value, taskId: selectedTask?.id || "" };
            }
            return { ...item, [field]: value };
          }),
        },
      };
    });
  };

  const handleAddWorkItem = (dayId) => {
    setWeekDraft((current) => {
      const dayEntry = current[dayId] || {};
      return {
        ...current,
        [dayId]: {
          ...dayEntry,
          items: [...(dayEntry.items || []), createWorkItem()],
        },
      };
    });
  };

  const handleRemoveWorkItem = (dayId, itemKey) => {
    setWeekDraft((current) => {
      const dayEntry = current[dayId] || {};
      const removedItem = (dayEntry.items || []).find((item) => getWorkItemRowKey(item) === itemKey);
      const entryWithDelete = withDeletedWorkItem(dayEntry, removedItem);
      const remainingItems = (dayEntry.items || []).filter((item) => getWorkItemRowKey(item) !== itemKey);
      return {
        ...current,
        [dayId]: {
          ...entryWithDelete,
          items: remainingItems.length ? remainingItems : [createWorkItem()],
        },
      };
    });
  };

  const handleExpenseSubmit = async (event) => {
    event.preventDefault();
    if (isActionLoading || isExpenseReadOnly || !expenseTitle.trim() || !expenseDescription.trim() || !hasValidExpenseAmount) {
      return;
    }
    const didSubmit = await onSubmitExpense({
      expenseId: selectedExpense?.id || "",
      title: expenseTitle.trim(),
      amount: expenseAmountNumber,
      description: expenseDescription.trim(),
      expenseDate,
      billFile: expenseBillFile,
      category: selectedExpense?.category || "Other",
      resubmit: isExpenseMoreInformation,
    });
    if (didSubmit !== false && expenseBillFile) {
      setExpenseBillFile(null);
    }
  };

  const handleHrSubmit = async (event) => {
    event.preventDefault();
    if (isActionLoading || !requestSubject.trim()) {
      return;
    }
    const didSubmit = await onSubmitRequest({
      requestType,
      subject: requestSubject.trim(),
      description: requestDescription.trim(),
    });
    if (didSubmit !== false) {
      setRequestSubject("");
      setRequestDescription("");
      setRequestType(REQUEST_TYPE_OPTIONS[0]);
    }
  };

  const handleBulkRowChange = (rowId, field, value) => {
    setBulkRows((currentRows) =>
      currentRows.map((row) => {
        if (row.id !== rowId) {
          return row;
        }
        if (field === "project") {
          return {
            ...row,
            project: value,
            task: "",
          };
        }
        return {
          ...row,
          [field]: value,
        };
      })
    );
  };

  const handleAddBulkRow = () => {
    setBulkRows((currentRows) => [...currentRows, createBulkTimeRow()]);
  };

  const handleRemoveBulkRow = (rowId) => {
    setBulkRows((currentRows) => {
      const remainingRows = currentRows.filter((row) => row.id !== rowId);
      return remainingRows.length ? remainingRows : [{ ...createBulkTimeRow(), hours: "8" }];
    });
  };

  const handleBulkTimeSubmit = (event) => {
    event.preventDefault();

    if (
      isActionLoading ||
      isTimesheetLocked ||
      !selectedTimesheetCellCount ||
      !bulkRowsValidation.isValid
    ) {
      return;
    }

    onFillSelectedTime({
      entries: bulkRowsValidation.normalizedRows.map((row) => ({
        project: row.selectedProject.name || row.project,
        projectId: getProjectOptionId(row.selectedProject),
        task: row.selectedTask?.name || "",
        taskId: row.selectedTask?.id || "",
        description: row.description,
        hours: row.numericHours,
      })),
    });
  };

  return (
    <div className="new-hrm-modal-backdrop" role="presentation">
      <section
        className={`new-hrm-modal new-hrm-loader-host ${action === "time" ? "new-hrm-modal--time" : ""} ${action === "timeEntry" ? "new-hrm-modal--time-entry" : ""} ${action === "bulkTime" ? "new-hrm-modal--bulk-time" : ""} ${action === "timesheet" ? "new-hrm-modal--timesheet-review" : ""}`}
        role="dialog"
        aria-modal="true"
      >
        <ComponentLoader active={isActionLoading} label={modalLoadingLabel} />
        <header className="new-hrm-modal__header">
          <div>
            <h2>{modalTitle}</h2>
            <p>{modalDescription}</p>
          </div>
          <button type="button" className="new-hrm-icon-button" onClick={() => onClose()} aria-label="Close">
            <FiX aria-hidden="true" />
          </button>
        </header>

        {action === "leave" && (
          <form className="new-hrm-form-grid new-hrm-leave-form" onSubmit={handleLeaveSubmit}>
            <div className="new-hrm-form-steps" aria-label="Leave request progress">
              <span className={leaveStep === 1 ? "is-active" : "is-complete"}>1. Leave details</span>
              <span className={leaveStep === 2 ? "is-active" : ""}>2. OOO confirmation</span>
            </div>
            {isLeaveRejected && selectedLeaveRequest?.rejectionReason && (
              <div className="new-hrm-form-notice new-hrm-form-notice--rejected" role="status">
                <strong>Rejection reason</strong>
                <p>{selectedLeaveRequest.rejectionReason}</p>
              </div>
            )}
            {isLeaveReadOnly && !isLeaveRejected && (
              <div className="new-hrm-form-notice" role="status">
                <strong>{selectedLeaveRequest?.status || "Locked"}</strong>
                <p>{isLeaveApproved ? "Approved leave cannot be changed or cancelled. Raise an HR case if a correction is required." : "This leave request cannot be changed, cancelled, or resubmitted."}</p>
              </div>
            )}
            {leaveStep === 1 && (
              <>
                <div className="new-hrm-leave-form__dates">
              <label className="new-hrm-field">
                <span>From</span>
                <input
                  type="date"
                  disabled={isActionLoading || isLeaveReadOnly}
                  value={leaveStartDate}
                  onChange={(event) => {
                    const nextStartDate = event.target.value;
                    setLeaveStartDate(nextStartDate);
                    if (leaveEndDate < nextStartDate) {
                      setLeaveEndDate(nextStartDate);
                    }
                  }}
                />
              </label>
              <label className="new-hrm-field">
                <span>To</span>
                <input
                  min={leaveStartDate}
                  type="date"
                  disabled={isActionLoading || isLeaveReadOnly}
                  value={leaveEndDate}
                  onChange={(event) => setLeaveEndDate(event.target.value)}
                />
              </label>
              <div className="new-hrm-leave-duration">
                <span>Duration</span>
                <strong>{leaveDays ? formatLeaveDuration(leaveDays) : "--"}</strong>
              </div>
              {hasWeekendConflict && (
                <small className="new-hrm-leave-validation new-hrm-leave-validation--error new-hrm-leave-holiday-validation" role="alert">
                  {leaveWeekendMessage}
                </small>
              )}
              {hasHolidayConflict && (
                <small className="new-hrm-leave-validation new-hrm-leave-validation--error new-hrm-leave-holiday-validation" role="alert">
                  {leaveHolidayMessage}
                </small>
              )}
              {hasLeaveOverlapConflict && (
                <small className="new-hrm-leave-validation new-hrm-leave-validation--error new-hrm-leave-holiday-validation" role="alert">
                  {leaveOverlapMessage}
                </small>
              )}
              {isSingleDayLeave && (
                <label className="new-hrm-leave-half-day">
                  <input
                    type="checkbox"
                    checked={leaveHalfDay}
                    disabled={isActionLoading || isLeaveReadOnly}
                    onChange={(event) => setLeaveHalfDay(event.target.checked)}
                  />
                  <span>Apply as half day</span>
                </label>
              )}
                </div>
                <div className="new-hrm-leave-form__details">
              <label className="new-hrm-field">
                <span>Leave Type</span>
                <select value={leaveType} onChange={(event) => setLeaveType(event.target.value)} disabled={isActionLoading || isLeaveReadOnly}>
                  {leaveTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <small
                  className={`new-hrm-leave-validation ${hasLeaveValidationError ? "new-hrm-leave-validation--error" : ""}`}
                  role={hasLeaveValidationError ? "alert" : undefined}
                >
                  {leaveBalanceMessage}
                </small>
              </label>
              <label className="new-hrm-field">
                <span>Description</span>
                <textarea
                  required
                  rows="3"
                  disabled={isActionLoading || isLeaveReadOnly}
                  value={leaveDescription}
                  onChange={(event) => setLeaveDescription(event.target.value)}
                  placeholder="Reason for leave"
                />
              </label>
                </div>
              </>
            )}
            {leaveStep === 2 && !isLeaveReadOnly && (
              <section className="new-hrm-leave-confirmation" aria-labelledby="leave-ooo-confirmation-title">
                <div>
                  <span>Request summary</span>
                  <strong>{leaveType} · {formatLeaveDuration(leaveDays)}</strong>
                  <small>{leaveStartDate}{leaveEndDate !== leaveStartDate ? ` to ${leaveEndDate}` : ""}</small>
                </div>
                <label className="new-hrm-leave-declaration">
                  <input
                    type="checkbox"
                    checked={leaveOooAdded}
                    disabled={isActionLoading}
                    onChange={(event) => setLeaveOooAdded(event.target.checked)}
                  />
                  <span>
                    <strong id="leave-ooo-confirmation-title">OOO Added?</strong>
                    I have added OOO on all calendars and know that without OOO leave is going to be auto rejected.
                  </span>
                </label>
              </section>
            )}
            {(!isLeaveReadOnly || canCancelLeave || isLeaveApproved) && (
              <div className="new-hrm-form-actions">
                {canCancelLeave && (
                  <button
                    className="new-hrm-secondary-button new-hrm-secondary-button--danger"
                    type="button"
                    disabled={isActionLoading}
                    onClick={() => onCancelLeave?.(selectedLeaveRequest.id)}
                  >
                    Cancel Leave
                  </button>
                )}
                {isLeaveApproved && (
                  <button
                    className="new-hrm-secondary-button"
                    type="button"
                    disabled={isActionLoading}
                    onClick={() => onClose("attendanceRequest")}
                  >
                    Raise HR Case
                  </button>
                )}
                {!isLeaveReadOnly && leaveStep === 2 && (
                  <button className="new-hrm-secondary-button" type="button" disabled={isActionLoading} onClick={() => setLeaveStep(1)}>
                    Back
                  </button>
                )}
                {!isLeaveReadOnly && (
                  <button
                    className="new-hrm-primary-button"
                    type="submit"
                    disabled={
                      isActionLoading ||
                      !leaveDays ||
                      !leaveDescription.trim() ||
                      hasLeaveValidationError ||
                      (leaveStep === 2 && !leaveOooAdded)
                    }
                  >
                    {leaveStep === 1 ? "Continue" : selectedLeaveRequest ? "Save Changes" : "Submit Request"}
                  </button>
                )}
              </div>
            )}
          </form>
        )}

        {action === "time" && (
          <form className="new-hrm-form-grid new-hrm-form-grid--time" onSubmit={handleTimeSubmit}>
            <div className="new-hrm-timesheet-context">
              <label className="new-hrm-field new-hrm-field--compact">
                <select
                  aria-label="Timesheet month"
                  disabled={isActionLoading}
                  value={selectedTimesheetMonthId}
                  onChange={(event) => onTimesheetMonthChange(event.target.value)}
                >
                  {timesheetMonths.map((month) => (
                    <option key={month.id} value={month.id}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </label>
              <span className={`new-hrm-chip ${getStatusChipClass(selectedMonthStatus)}`}>
                {getStatusLabel(selectedMonthStatus)}
              </span>
              <button className="new-hrm-secondary-button" type="button" onClick={onSubmitTimesheetApproval} disabled={isActionLoading || isTimesheetLocked || !selectedTimesheetCellCount || hasSelectedSubmitHourError}>
                Submit Selected{selectedTimesheetCellCount ? ` (${selectedTimesheetCellCount})` : ""}
              </button>
            </div>
            {selectedSubmitHourMessage && (
              <p className="new-hrm-week-log-error" role="alert">
                {selectedSubmitHourMessage}
              </p>
            )}
            {isTimesheetLocked && (
              <p className="new-hrm-timesheet-lock-note">
                {timesheetLockMessage || `${selectedTimesheetMonth?.label} cannot be edited.`}
              </p>
            )}
            <div className="new-hrm-week-log-header">
              <div>
                <strong>{selectedTimeWeek?.label}</strong>
                <span>{selectedTimeWeek?.range}</span>
              </div>
              <p>{monthDays.length} days</p>
            </div>
            <div className="new-hrm-week-log-grid">
              {monthDays.map((day) => {
                const dayEntry = weekDraft[day.id] || { items: [createWorkItem()] };
                const dayHours = getDayEntryHours(dayEntry);
                const dayStatus = getEntryStatus(dayEntry, selectedTimesheetMonth);
                const isOutsideMonth = dayEntry?.isOutsideMonth || !dayEntry?.workDate;
                const isWeekend = isWeekendDateValue(dayEntry?.workDate);
                const isDayLocked = isTimesheetLocked || isOutsideMonth || isLockedTimeStatus(dayStatus);
                const isOverDailyLimit = dayHours > MAX_DAILY_HOURS;
                return (
                  <div
                    key={day.id}
                    className={`new-hrm-week-log-row new-hrm-week-log-row--${isOutsideMonth ? "outside" : dayStatus.toLowerCase()} ${selectedTimeDayId === day.id ? "new-hrm-week-log-row--selected" : ""} ${isWeekend ? "new-hrm-week-log-row--weekend" : ""} ${isOverDailyLimit ? "new-hrm-week-log-row--error" : ""}`}
                  >
                    <header className="new-hrm-week-log-row__header">
                      <div>
                        <strong>{day.label}</strong>
                        <span className={isOverDailyLimit ? "new-hrm-week-log-row__limit-error" : ""}>
                          {isOutsideMonth ? "Outside selected month" : dayHours ? `${formatDayEntryHours(dayEntry)} / ${MAX_DAILY_HOURS} hours` : isWeekend ? "Weekend" : "No hours"}
                        </span>
                        {!isOutsideMonth && <em>{getStatusLabel(dayStatus)}</em>}
                      </div>
                      <button type="button" onClick={() => handleAddWorkItem(day.id)} disabled={isActionLoading || isDayLocked}>
                        <FiPlus aria-hidden="true" />
                        <span>Add row</span>
                      </button>
                    </header>
                    {isOverDailyLimit && (
                      <p className="new-hrm-week-log-row__error" role="alert">
                        Daily total cannot exceed {MAX_DAILY_HOURS} hours.
                      </p>
                    )}
                    {!isOutsideMonth && dayEntry.items.map((item, index) => {
                      const itemKey = getWorkItemRowKey(item);
                      const itemStatus = item.status || dayStatus;
                      const isItemLocked = isReadOnlyTimeItem(item, itemStatus, isDayLocked);
                      const selectedProject = findProjectOptionForItem(item, projectOptions);
                      const rowTaskOptions = getTaskOptionsForProject(selectedProject, taskOptions);
                      const taskDatalistId = getTaskDatalistId("new-hrm-task-options", day.id, itemKey);
                      const itemReviewComment = isRejectedEntryStatus(itemStatus) ? getReviewComment(item) : "";
                      return (
                        <div key={itemKey} className="new-hrm-week-work-row">
                          <label className="new-hrm-field new-hrm-field--compact new-hrm-week-work-row__project">
                            <span>Project</span>
                            <input
                              list="new-hrm-project-options"
                              disabled={isActionLoading || isItemLocked}
                              value={item.project}
                              onChange={(event) => handleWeekItemChange(day.id, itemKey, "project", event.target.value)}
                              placeholder="Search project"
                            />
                          </label>
                          <label className="new-hrm-field new-hrm-field--compact new-hrm-week-work-row__task">
                            <span>Task</span>
                            <input
                              list={taskDatalistId}
                              disabled={isActionLoading || isItemLocked || !selectedProject}
                              value={item.task}
                              onChange={(event) => handleWeekItemChange(day.id, itemKey, "task", event.target.value)}
                              placeholder={selectedProject ? "Optional task" : "Select project first"}
                            />
                            <datalist id={taskDatalistId}>
                              {rowTaskOptions.map((task) => (
                                <option key={task.id} value={task.name} />
                              ))}
                            </datalist>
                          </label>
                          <label className="new-hrm-field new-hrm-field--compact new-hrm-week-work-row__description">
                            <span>Notes</span>
                            <textarea
                              autoFocus={selectedTimeDayId === day.id && index === 0}
                              disabled={isActionLoading || isItemLocked}
                              rows={2}
                              value={item.description}
                              onChange={(event) => handleWeekItemChange(day.id, itemKey, "description", event.target.value)}
                              placeholder="Optional note"
                            />
                          </label>
                          <label className="new-hrm-field new-hrm-field--compact new-hrm-week-work-row__hours">
                            <span>Hours</span>
                            <input
                              min="0"
                              max={MAX_DAILY_HOURS}
                              step="0.25"
                              type="number"
                              disabled={isActionLoading || isItemLocked}
                              value={item.hours}
                              onChange={(event) => handleWeekItemChange(day.id, itemKey, "hours", event.target.value)}
                            />
                          </label>
                          <button
                            className="new-hrm-week-work-row__remove"
                            type="button"
                            disabled={isActionLoading || isItemLocked}
                            onClick={() => handleRemoveWorkItem(day.id, itemKey)}
                            aria-label={`Remove ${day.label} work row`}
                          >
                            <FiTrash2 aria-hidden="true" />
                          </button>
                          {itemReviewComment && (
                            <div className="new-hrm-week-log-row__review new-hrm-week-work-row__review" role="note">
                              <strong>TSE rejection reason</strong>
                              <p>{itemReviewComment}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            <datalist id="new-hrm-project-options">
              {projectOptions.map((project) => (
                <option key={project.id} value={project.name} />
              ))}
            </datalist>
            {hasDailyLimitError && (
              <p className="new-hrm-week-log-error" role="alert">
                Reduce hours for {daysOverDailyLimit.map((day) => day.label).join(", ")} before saving.
              </p>
            )}
            <button className="new-hrm-primary-button new-hrm-week-log-submit" type="submit" disabled={isActionLoading || hasDailyLimitError || isTimesheetLocked}>
              Save Full Week
            </button>
          </form>
        )}

        {action === "timeEntry" && (
          <form className="new-hrm-form-grid new-hrm-time-entry-form" onSubmit={handleTimeEntrySubmit}>
            <div className="new-hrm-time-entry-summary">
              <div>
                <span>{selectedTimeWeek?.label || "Selected week"}</span>
                <strong>{selectedTimeDay?.label || "Selected day"}</strong>
                <small>{selectedDayEntry?.workDate || selectedTimesheetMonth?.label || ""}</small>
              </div>
              <span className={`new-hrm-chip ${getStatusChipClass(selectedDayStatus)}`}>
                {getStatusLabel(selectedDayStatus)}
              </span>
              <strong>{formatDayHoursSummary(selectedDayEntry)}</strong>
            </div>
            {isSelectedDayLocked && (
              <p className="new-hrm-timesheet-lock-note">
                {timesheetLockMessage || "This time entry cannot be edited."}
              </p>
            )}
            {selectedDayHours > MAX_DAILY_HOURS && (
              <p className="new-hrm-week-log-row__error" role="alert">
                Daily total cannot exceed {MAX_DAILY_HOURS} hours.
              </p>
            )}
            <div className="new-hrm-time-entry-rows">
              {(selectedDayEntry?.items || []).map((item, index) => {
                const itemKey = getWorkItemRowKey(item);
                const itemStatus = item.status || selectedDayStatus;
                const isItemLocked = isReadOnlyTimeItem(item, itemStatus, isSelectedDayLocked);
                const projectColor = getWorkItemProjectColor(item, projectOptions);
                const selectedProject = findProjectOptionForItem(item, projectOptions);
                const rowTaskOptions = getTaskOptionsForProject(selectedProject, taskOptions);
                const taskDatalistId = getTaskDatalistId("new-hrm-entry-task-options", selectedTimeDayId, itemKey);
                const itemReviewComment = isRejectedEntryStatus(itemStatus) ? getReviewComment(item) : "";
                return (
                  <div
                    key={itemKey}
                    className="new-hrm-week-work-row new-hrm-time-entry-row"
                    style={projectColor ? { "--entry-project-color": projectColor } : undefined}
                  >
                    {projectColor && <span className="new-hrm-time-entry-row__project-color" aria-hidden="true" />}
                    <label className="new-hrm-field new-hrm-field--compact new-hrm-week-work-row__project">
                      <span>Project</span>
                      <input
                        list="new-hrm-entry-project-options"
                        disabled={isActionLoading || isItemLocked}
                        value={item.project}
                        onChange={(event) => handleWeekItemChange(selectedTimeDayId, itemKey, "project", event.target.value)}
                        placeholder="Search project"
                      />
                    </label>
                    <label className="new-hrm-field new-hrm-field--compact new-hrm-week-work-row__task">
                      <span>Project Entity</span>
                      <input
                        list={taskDatalistId}
                        disabled={isActionLoading || isItemLocked || !selectedProject}
                        value={item.task}
                        onChange={(event) => handleWeekItemChange(selectedTimeDayId, itemKey, "task", event.target.value)}
                        placeholder={selectedProject ? "Optional entity" : "Select project first"}
                      />
                      <datalist id={taskDatalistId}>
                        {rowTaskOptions.map((task) => (
                          <option key={task.id} value={task.name} />
                        ))}
                      </datalist>
                    </label>
                    <label className="new-hrm-field new-hrm-field--compact new-hrm-week-work-row__description">
                      <span>Description</span>
                      <textarea
                        autoFocus={index === 0}
                        disabled={isActionLoading || isItemLocked}
                        rows={4}
                        value={item.description}
                        onChange={(event) => handleWeekItemChange(selectedTimeDayId, itemKey, "description", event.target.value)}
                        placeholder="Work details"
                      />
                    </label>
                    <label className="new-hrm-field new-hrm-field--compact new-hrm-week-work-row__hours">
                      <span>Hours</span>
                      <input
                        min="0"
                        max={MAX_DAILY_HOURS}
                        step="0.25"
                        type="number"
                        disabled={isActionLoading || isItemLocked}
                        value={item.hours}
                        onChange={(event) => handleWeekItemChange(selectedTimeDayId, itemKey, "hours", event.target.value)}
                      />
                    </label>
                    <button
                      className="new-hrm-week-work-row__remove"
                      type="button"
                      disabled={isActionLoading || isItemLocked}
                      onClick={() => handleRemoveWorkItem(selectedTimeDayId, itemKey)}
                      aria-label={`Remove ${selectedTimeDay?.label || "selected day"} work row`}
                    >
                      <FiTrash2 aria-hidden="true" />
                    </button>
                    {itemReviewComment && (
                      <div className="new-hrm-week-log-row__review new-hrm-week-work-row__review" role="note">
                        <strong>TSE rejection reason</strong>
                        <p>{itemReviewComment}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <datalist id="new-hrm-entry-project-options">
              {projectOptions.map((project) => (
                <option key={project.id} value={project.name} />
              ))}
            </datalist>
            <div className="new-hrm-time-entry-actions">
              <button type="button" className="new-hrm-secondary-button" onClick={() => handleAddWorkItem(selectedTimeDayId)} disabled={isActionLoading || isSelectedDayLocked}>
                <FiPlus aria-hidden="true" />
                Add Row
              </button>
              <button className="new-hrm-primary-button" type="submit" disabled={isActionLoading || hasDailyLimitError || isSelectedDayLocked}>
                Save Entry
              </button>
            </div>
          </form>
        )}

        {action === "bulkTime" && (
          <form className="new-hrm-form-grid new-hrm-bulk-time-form" onSubmit={handleBulkTimeSubmit}>
            <div className="new-hrm-bulk-time-summary">
              <div className="new-hrm-bulk-time-summary__copy">
                <strong>{selectedTimesheetCellCount} selected {selectedTimesheetCellCount === 1 ? "date" : "dates"}</strong>
                <span>{selectedTimesheetMonth?.label || "Selected month"}</span>
              </div>
              <div className="new-hrm-bulk-time-selected-dates" aria-label="Selected dates">
                {selectedBulkTimeDates.length ? (
                  selectedBulkTimeDates.map((date) => (
                    <span className="new-hrm-bulk-time-date-chip" key={date.cellId}>
                      {date.label}
                    </span>
                  ))
                ) : (
                  <span className="new-hrm-bulk-time-date-chip new-hrm-bulk-time-date-chip--empty">
                    No dates selected
                  </span>
                )}
              </div>
            </div>
            {isTimesheetLocked && (
              <p className="new-hrm-timesheet-lock-note">
                {timesheetLockMessage || `${selectedTimesheetMonth?.label} cannot be edited.`}
              </p>
            )}
            <div className="new-hrm-bulk-time-rows">
              {bulkRows.map((row, index) => {
                const selectedProject = findProjectOptionForItem({ project: row.project }, projectOptions);
                const rowTaskOptions = getTaskOptionsForProject(selectedProject, taskOptions);
                const taskDatalistId = getTaskDatalistId("new-hrm-bulk-task-options", "row", row.id);
                return (
                  <div className="new-hrm-bulk-time-row" key={row.id}>
                    <strong>Row {index + 1}</strong>
                    <label className="new-hrm-field">
                      <span>Project Name</span>
                      <input
                        list="new-hrm-bulk-project-options"
                        required
                        disabled={isActionLoading || isTimesheetLocked}
                        value={row.project}
                        onChange={(event) => handleBulkRowChange(row.id, "project", event.target.value)}
                        placeholder="Search project"
                      />
                    </label>
                    <label className="new-hrm-field">
                      <span>Project Entity</span>
                      <input
                        list={taskDatalistId}
                        disabled={isActionLoading || isTimesheetLocked || !selectedProject}
                        value={row.task}
                        onChange={(event) => handleBulkRowChange(row.id, "task", event.target.value)}
                        placeholder={selectedProject ? "Search entity" : "Select project first"}
                      />
                      <datalist id={taskDatalistId}>
                        {rowTaskOptions.map((task) => (
                          <option key={task.id} value={task.name} />
                        ))}
                      </datalist>
                    </label>
                    <label className="new-hrm-field new-hrm-bulk-time-description">
                      <span>Description</span>
                      <textarea
                        required
                        disabled={isActionLoading || isTimesheetLocked}
                        rows="3"
                        value={row.description}
                        onChange={(event) => handleBulkRowChange(row.id, "description", event.target.value)}
                        placeholder="Work description"
                      />
                    </label>
                    <label className="new-hrm-field">
                      <span>Hours</span>
                      <input
                        min="0.25"
                        max={MAX_DAILY_HOURS}
                        step="0.25"
                        type="number"
                        required
                        disabled={isActionLoading || isTimesheetLocked}
                        value={row.hours}
                        onChange={(event) => handleBulkRowChange(row.id, "hours", event.target.value)}
                      />
                    </label>
                    <button
                      className="new-hrm-week-work-row__remove"
                      type="button"
                      disabled={isActionLoading || isTimesheetLocked}
                      onClick={() => handleRemoveBulkRow(row.id)}
                      aria-label={`Remove bulk fill row ${index + 1}`}
                    >
                      <FiTrash2 aria-hidden="true" />
                    </button>
                  </div>
                );
              })}
            </div>
            <datalist id="new-hrm-bulk-project-options">
              {projectOptions.map((project) => (
                <option key={project.id} value={project.name} />
              ))}
            </datalist>
            <div className="new-hrm-bulk-time-actions">
              <span className={bulkRowsValidation.hasHourLimitError ? "new-hrm-week-log-row__limit-error" : ""}>
                {String(Number(bulkRowsValidation.totalHours.toFixed(2)))} / {MAX_DAILY_HOURS} hours per selected date
              </span>
              <button type="button" className="new-hrm-secondary-button" onClick={handleAddBulkRow} disabled={isActionLoading || isTimesheetLocked}>
                <FiPlus aria-hidden="true" />
                Add Row
              </button>
            </div>
            {bulkRowsValidation.hasHourLimitError && (
              <p className="new-hrm-week-log-error" role="alert">
                Fill Selected cannot add more than {MAX_DAILY_HOURS} hours per date.
              </p>
            )}
            <button
              className="new-hrm-primary-button"
              type="submit"
              disabled={
                isActionLoading ||
                isTimesheetLocked ||
                !selectedTimesheetCellCount ||
                !bulkRowsValidation.isValid
              }
            >
              Save to Selected Dates
            </button>
          </form>
        )}

        {action === "expense" && (
          <form className="new-hrm-expense-form" onSubmit={handleExpenseSubmit}>
            {isExpenseRejected && selectedExpense?.rejectionReason && (
              <div className="new-hrm-form-notice new-hrm-form-notice--rejected" role="status">
                <strong>Rejection reason</strong>
                <p>{selectedExpense.rejectionReason}</p>
              </div>
            )}
            {isExpenseMoreInformation && (
              <div className="new-hrm-form-notice new-hrm-form-notice--information" role="status">
                <strong>More information required</strong>
                <p>{selectedExpense?.rejectionReason || "Update the description with the requested information, then resubmit the reimbursement."}</p>
              </div>
            )}
            {isExpenseReadOnly && !isExpenseRejected && (
              <div className="new-hrm-form-notice" role="status">
                <strong>{selectedExpense?.status || "Locked"}</strong>
                <p>{isExpenseApproved ? "Approved reimbursement cannot be changed or cancelled. Raise a Finance case if a correction is required." : "This reimbursement cannot be changed, cancelled, or resubmitted."}</p>
              </div>
            )}
            <div className="new-hrm-expense-form__grid">
              <label className="new-hrm-field">
                <span>Expense</span>
                <input value={expenseTitle} onChange={(event) => setExpenseTitle(event.target.value)} disabled={isActionLoading || isExpenseReadOnly} />
              </label>
              <label className="new-hrm-field">
                <span>Amount</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0.01"
                  step="0.01"
                  aria-label="Amount"
                  value={expenseAmount}
                  onChange={(event) => setExpenseAmount(event.target.value)}
                  disabled={isActionLoading || isExpenseReadOnly}
                  aria-invalid={showExpenseAmountError ? "true" : undefined}
                  aria-describedby={showExpenseAmountError ? "expense-amount-error" : undefined}
                />
                {showExpenseAmountError && (
                  <small id="expense-amount-error" className="new-hrm-leave-validation new-hrm-leave-validation--error" role="alert">
                    Expense amount must be greater than 0.
                  </small>
                )}
              </label>
              <label className="new-hrm-field">
                <span>Expense Date</span>
                <input type="date" value={expenseDate} onChange={(event) => setExpenseDate(event.target.value)} disabled={isActionLoading || isExpenseReadOnly} />
              </label>
              <label className="new-hrm-field new-hrm-expense-description">
                <span>Description</span>
                <textarea
                  required
                  rows="4"
                  value={expenseDescription}
                  onChange={(event) => setExpenseDescription(event.target.value)}
                  disabled={isActionLoading || isExpenseReadOnly}
                  placeholder={isExpenseMoreInformation ? "Add the information requested by the reviewer" : "Explain the expense and its business purpose"}
                />
              </label>
              <label className="new-hrm-expense-bill">
                <span>Bill Attachment</span>
                <input
                  type="file"
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={(event) => setExpenseBillFile(event.target.files?.[0] || null)}
                  disabled={isActionLoading || isExpenseReadOnly}
                />
                <div>
                  <strong>{expenseBillFile ? expenseBillFile.name : "Attach bill or receipt"}</strong>
                  <small>{expenseBillFile ? "Ready to upload with expense" : "Image, PDF, DOC, or DOCX"}</small>
                  <em>Browse</em>
                </div>
              </label>
            </div>
            {(!isExpenseReadOnly || canCancelExpense || isExpenseApproved) && (
              <div className="new-hrm-expense-form__actions">
                <span>{expenseBillFile ? "Bill will be linked to this reimbursement in CRM." : "Attach a bill when supporting proof is available."}</span>
                <div className="new-hrm-expense-form__buttons">
                  {canCancelExpense && (
                    <button
                      className="new-hrm-secondary-button new-hrm-secondary-button--danger"
                      type="button"
                      disabled={isActionLoading}
                      onClick={() => onCancelExpense?.(selectedExpense.id)}
                    >
                      Cancel Expense
                    </button>
                  )}
                  {isExpenseApproved && (
                    <button
                      className="new-hrm-secondary-button"
                      type="button"
                      disabled={isActionLoading}
                      onClick={() => onClose("financeRequest")}
                    >
                      Raise Finance Case
                    </button>
                  )}
                  {!isExpenseReadOnly && (
                    <button
                      className="new-hrm-primary-button"
                      type="submit"
                      disabled={isActionLoading || !expenseTitle.trim() || !expenseDescription.trim() || !hasValidExpenseAmount}
                    >
                      {isExpenseMoreInformation ? "Save and Resubmit" : selectedExpense ? "Save Changes" : "Submit Reimbursement"}
                    </button>
                  )}
                </div>
              </div>
            )}
          </form>
        )}

        {(action === "hr" || action === "assetRequest" || action === "financeRequest" || action === "attendanceRequest") && (
          <form className="new-hrm-request-create-form new-hrm-request-create-form--case" onSubmit={handleHrSubmit}>
            <div className="new-hrm-request-create-header">
              <div>
                <span>Case intake</span>
                <h3>{action === "assetRequest" ? "Asset Request" : action === "financeRequest" ? "Finance Case" : action === "attendanceRequest" ? "Attendance Case" : "New Request"}</h3>
                <p>Submit a structured Salesforce case with ownership routed by request type.</p>
              </div>
              <em>{requestType}</em>
            </div>
            <div className="new-hrm-request-create-grid">
              <label className="new-hrm-field">
                <span>Request Type</span>
                <select value={requestType} onChange={(event) => setRequestType(event.target.value)} disabled={isActionLoading}>
                  {REQUEST_TYPE_OPTIONS.map((option) => (
                    <option key={option}>{option}</option>
                  ))}
                </select>
              </label>
              <label className="new-hrm-field">
                <span>Subject</span>
                <input
                  value={requestSubject}
                  onChange={(event) => setRequestSubject(event.target.value)}
                  placeholder="Short summary"
                  disabled={isActionLoading}
                />
              </label>
              <label className="new-hrm-field new-hrm-request-create-grid__wide">
                <span>Description</span>
                <textarea
                  value={requestDescription}
                  onChange={(event) => setRequestDescription(event.target.value)}
                  placeholder="Add the details HR or Operations should review"
                  rows="5"
                  disabled={isActionLoading}
                />
              </label>
            </div>
            <div className="new-hrm-request-create-actions">
              <span>
                <FiInfo aria-hidden="true" />
                Description locks after creation. Continue the conversation in case comments.
              </span>
              <button className="new-hrm-primary-button" type="submit" disabled={isActionLoading || !requestSubject.trim()}>
                <FiSend aria-hidden="true" />
                Create Request
              </button>
            </div>
            <div className="new-hrm-request-create-recent">
              <div>
                <strong>Recent requests</strong>
                <span>{requests.length ? `${Math.min(requests.length, 3)} shown` : "No recent requests"}</span>
              </div>
              {requests.slice(0, 3).map((request) => (
                <article key={request.id}>
                  <strong>{request.requestNumber}</strong>
                  <span>{request.subject}</span>
                  <em className={`new-hrm-status new-hrm-status--${getStatusClass(request)}`}>{request.status}</em>
                </article>
              ))}
            </div>
          </form>
        )}

        {action === "payslip" && (
          <div className="new-hrm-modal-list">
            {currentPayslip ? (
              <>
                <div className="new-hrm-list-row">
                  <span>{currentPayslip.period} Salary Slip</span>
                  <strong>{currentPayslip.net}</strong>
                  <em className="new-hrm-status new-hrm-status--posted">{currentPayslip.status}</em>
                </div>
                <div className="new-hrm-list-row">
                  <span>Gross Salary</span>
                  <strong>{currentPayslip.gross}</strong>
                </div>
                <div className="new-hrm-list-row">
                  <span>Deductions</span>
                  <strong>{currentPayslip.deductions}</strong>
                </div>
                <div className="new-hrm-list-row">
                  <span>Tax</span>
                  <strong>{currentPayslip.tax}</strong>
                </div>
                <div className="new-hrm-list-row">
                  <span>Pay Date</span>
                  <strong>{currentPayslip.payDate}</strong>
                </div>
                <button className="new-hrm-primary-button" type="button" onClick={() => onDownloadPayslip(currentPayslip)} disabled={isActionLoading}>
                  <FiDownload aria-hidden="true" />
                  <span>Download Salary Slip</span>
                </button>
              </>
            ) : (
              <p className="new-hrm-empty-state">No posted salary slip is available yet.</p>
            )}
          </div>
        )}

        {action === "quick" && (
          <div className="new-hrm-action-grid">
            {["leave", "time", "expense", "hr"].map((quickAction) => (
              <button key={quickAction} type="button" onClick={() => onClose(quickAction)}>
                <strong>{actionCatalog[quickAction].title}</strong>
                <span>{actionCatalog[quickAction].description}</span>
              </button>
            ))}
          </div>
        )}

        {action === "timesheet" && (
          <div className="new-hrm-modal-timesheet">
            <div className="new-hrm-timesheet-review__toolbar">
              <div className="new-hrm-timesheet-review__filters">
                <label className="new-hrm-field new-hrm-field--compact">
                  <select
                    aria-label="Timesheet month"
                    disabled={isActionLoading}
                    value={selectedTimesheetMonthId}
                    onChange={(event) => onTimesheetMonthChange(event.target.value)}
                  >
                    {timesheetMonths.map((month) => (
                      <option key={month.id} value={month.id}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </label>
                <span className={`new-hrm-chip ${getStatusChipClass(selectedMonthStatus)}`}>
                  {getStatusLabel(selectedMonthStatus)}
                </span>
              </div>
              <div className="new-hrm-timesheet-review__summary">
                <span>
                  <strong>{formattedReviewHours}h</strong>
                  <small>logged</small>
                </span>
                <span>
                  <strong>{selectedTimesheetCellCount}</strong>
                  <small>selected</small>
                </span>
                <button className="new-hrm-primary-button" type="button" onClick={onSubmitTimesheetApproval} disabled={isActionLoading || isTimesheetLocked || !selectedTimesheetCellCount || hasSelectedSubmitHourError}>
                  Submit Selected{selectedTimesheetCellCount ? ` (${selectedTimesheetCellCount})` : ""}
                </button>
              </div>
            </div>
            {selectedSubmitHourMessage && (
              <p className="new-hrm-week-log-error" role="alert">
                {selectedSubmitHourMessage}
              </p>
            )}

            <div className="new-hrm-timesheet-review__legend" aria-label="Timesheet status legend">
              <span><i className="new-hrm-timesheet-review__legend-dot new-hrm-timesheet-review__legend-dot--draft" />Draft {timesheetReviewSummary.draft}</span>
              <span><i className="new-hrm-timesheet-review__legend-dot new-hrm-timesheet-review__legend-dot--submitted" />In Review {timesheetReviewSummary.submitted}</span>
              <span><i className="new-hrm-timesheet-review__legend-dot new-hrm-timesheet-review__legend-dot--approved" />Approved {timesheetReviewSummary.approved}</span>
              <span><i className="new-hrm-timesheet-review__legend-dot new-hrm-timesheet-review__legend-dot--rejected" />Rejected {timesheetReviewSummary.rejected}</span>
            </div>

            <div className="new-hrm-timesheet-review__table" role="table" aria-label={`${selectedTimesheetMonth?.label || "Selected month"} timesheet`}>
              <div className="new-hrm-timesheet-review__head" role="row">
                <div role="columnheader">Week</div>
                {monthDays.map((day) => (
                  <div key={day.id} role="columnheader">
                    <strong>{day.short}</strong>
                    <span>{day.label}</span>
                  </div>
                ))}
              </div>
              {monthTimesheet.map((week) => (
                <div className="new-hrm-timesheet-review__row" key={week.id} role="row">
                  <div className="new-hrm-timesheet-review__week" role="rowheader">
                    <strong>{week.label}</strong>
                    <span>{week.range}</span>
                  </div>
                  {monthDays.map((day) => {
                    const entry = week.entries[day.id];
                    const status = getEntryStatus(entry, selectedTimesheetMonth);
                    const cellId = `${selectedTimesheetMonthId}:${week.id}:${day.id}`;
                    const isSelected = selectedTimesheetCellIds.includes(cellId);
                    const isOutsideMonth = entry?.isOutsideMonth || !entry?.workDate;
                    const isCellLocked = isTimesheetLocked || isLockedTimeStatus(status) || isOutsideMonth;
                    const isViewOnlyCell = isCellLocked && !isOutsideMonth;
                    return (
                      <button
                        key={`${week.id}-${day.id}`}
                        className={`new-hrm-timesheet-review-cell new-hrm-timesheet-review-cell--${isOutsideMonth ? "outside" : getStatusCssToken(status)} ${isSelected ? "new-hrm-timesheet-review-cell--selected" : ""} ${isCellLocked ? "new-hrm-timesheet-review-cell--locked" : ""} ${isViewOnlyCell ? "new-hrm-timesheet-review-cell--view-only" : ""}`}
                        type="button"
                        disabled={isActionLoading || isOutsideMonth}
                        onClick={() => {
                          if (isCellLocked) {
                            onTimeCellEdit?.(week.id, day.id);
                            return;
                          }
                          onTimesheetCellToggle(week.id, day.id);
                        }}
                        aria-pressed={isSelected}
                        aria-label={
                          isOutsideMonth
                            ? `${day.label} is outside ${selectedTimesheetMonth?.label || "selected month"}`
                            : isViewOnlyCell
                            ? `View ${day.label} ${week.label} timesheet box`
                            : isCellLocked
                            ? `${day.label} ${week.label} timesheet box locked`
                            : `Select ${day.label} ${week.label} ${getStatusLabel(status)} timesheet box`
                        }
                      >
                        <span>{formatWorkDateDay(entry)}</span>
                        <em>{isOutsideMonth ? "" : formatStatusWithHours(status, entry)}</em>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        {action === "policyDetail" && selectedPolicy && (
          <div className="new-hrm-modal-list">
            <div className="new-hrm-list-row">
              <span>Policy</span>
              <strong>{selectedPolicy.title}</strong>
            </div>
            <div className="new-hrm-list-row">
              <span>Updated</span>
              <strong>{selectedPolicy.date}</strong>
            </div>
            <p className="new-hrm-modal-note">
              This policy document is ready for the next implementation step.
            </p>
          </div>
        )}

        {action === "directory" && (
          <div className="new-hrm-modal-list">
            {directoryPeople.map((person) => (
              <div key={person.id} className="new-hrm-list-row">
                <span>{person.name}</span>
                <span>{person.role}</span>
                <strong>{person.team}</strong>
              </div>
            ))}
          </div>
        )}

        {action === "policies" && (
          <div className="new-hrm-modal-list">
            {policies.map((policy) => (
              <div key={policy.id} className="new-hrm-list-row">
                <span>{policy.title}</span>
                <span>Updated on {policy.date}</span>
                <strong>Open</strong>
              </div>
            ))}
          </div>
        )}

        {action === "onboarding" && (
          <div className="new-hrm-modal-list">
            {onboardingTasks.map((task) => (
              <label key={task.id} className="new-hrm-check-row">
                <input type="checkbox" checked={task.done} onChange={() => onToggleTask(task.id)} />
                <span>{task.label}</span>
              </label>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default ActionModal;
